import {safeLocalStorage} from '@utils/safeStorages';
import {UAParser} from 'ua-parser-js';
import {v4} from 'uuid';

import {LAST_AD_SHOWN} from '@web-catalog/components/Ads/SSP/consts';
import {TSSPAsset} from '@web-catalog/components/Ads/SSP/types';

type GetSppAdPayload = {
  userVkId?: number;
  sspUserId: string;
  sspSessionId: string;
  slotId: string;
};

const TABLET_DEVICE_TYPE = 1;
const MOBILE_DEVICE_TYPE = 4;
const DESKTOP_DEVICE_TYPE = 2;

const ONE_HOUR = 3600000;

const ASSET_MAP: {[key: number]: string} = {
  2: 'image',
  1: 'title',
  5: 'subtitle',
  4: 'caption',
};

const CONNECTION_TYPE_MAP: {[index: string]: number} = {
  'slow-2g': 4,
  '2g': 4,
  '3g': 5,
  '4g': 6,
  '5g': 7,
};

export const getTopDomain = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    return window?.top?.location.hostname;
  } catch (e) {
    const ancestorOrigins = window.location.ancestorOrigins;

    if (ancestorOrigins && ancestorOrigins.length > 0) {
      return new URL(ancestorOrigins[ancestorOrigins.length - 1]).hostname;
    }

    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
  }

  return window.location.hostname;
};

export const mapCreativeAssets = (assets: Array<TSSPAsset>) => {
  return assets.reduce(
    (mappedAssets: {[key: string]: TSSPAsset}, assetItem) => {
      mappedAssets[ASSET_MAP[assetItem.id]] = assetItem;

      return mappedAssets;
    },
    {},
  );
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const getSppAdPayload = ({
  userVkId,
  sspUserId,
  sspSessionId,
  slotId,
}: GetSppAdPayload) => {
  const parser = new UAParser(navigator.userAgent);

  function detectDeviceType() {
    if (parser.getDevice().type === 'mobile') {
      return MOBILE_DEVICE_TYPE;
    }

    if (parser.getDevice().type === 'tablet') {
      return TABLET_DEVICE_TYPE;
    }

    return DESKTOP_DEVICE_TYPE;
  }

  return {
    format: 'native',
    publisherid: '1',
    slotid: slotId,
    requestid: v4(),
    userid: sspUserId,
    ...(userVkId
      ? {
          vkid: String(userVkId),
        }
      : {}),
    sessionid: sspSessionId,
    device: {
      ua: navigator.userAgent,
      language: navigator.language,
      deviceType: detectDeviceType(),
      os: parser.getOS().name,
      osv: parser.getOS().version,
      ...(navigator.connection?.effectiveType
        ? {
            connectionType:
              CONNECTION_TYPE_MAP[navigator.connection?.effectiveType],
          }
        : {}),
    },
    site: {
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
      domain: getTopDomain(),
    },
  };
};

export const checkIsLastShowExpired = () => {
  const lastShown = Number(safeLocalStorage.getItem(LAST_AD_SHOWN));
  const hourAgo = Number(Date.now() - ONE_HOUR);

  return lastShown < hourAgo;
};
